/* Design constants for the offer PDF (Figma node `6406:2812`).
 *
 * The document is A4 in points, which is what the design is drawn in: the Figma
 * frame is 595 x 842 and jsPDF's `a4` in `pt` is 595.28 x 841.89. Every number
 * below is therefore a design coordinate used verbatim, with no scale factor in
 * between — the reason the generator is built in `pt` rather than jsPDF's
 * default millimetres.
 *
 * Vertical positions come in two flavours on purpose:
 * - fixed anchors (header, title, image band, footer) for the parts the design
 *   pins to the page;
 * - a rhythm (`SPEC_RHYTHM`) for the specification rows, because the number of
 *   option groups and selected accessories differs per config. Reproducing the
 *   design meant measuring the gaps between rows rather than their absolute y,
 *   so a config with four groups keeps the same spacing instead of overflowing
 *   a hard-coded table.
 *
 * Every value here was re-measured against the node on 2026-09-16 and the
 * measurement is written down in `docs/figma-parity.md`. Read that before
 * changing a number: it records which differences were chased, which were
 * accepted, and the two raw hexes the design uses without a variable behind
 * them.
 */

export const PDF_PAGE = {
  format: "a4",
  unit: "pt",
  width: 595,
  height: 842,
} as const;

export const PDF_COLORS = {
  /** `Grey 900/600/300` from the design's variables. */
  grey900: "#111111",
  grey600: "#686868",
  grey300: "#D0D0D0",
  /* The title and every bold figure sit one step lighter than `Grey 900`. It is
   * a raw hex in the Figma file with no variable behind it, so there is no name
   * to borrow. */
  figure: "#2A2A2A",
  /* Section rules are pure black, not `Grey 900` — also a raw value. */
  rule: "#000000",
} as const;

/** Logo and footer sit on the page margin; everything else on the content edge. */
export const PDF_MARGIN = {
  outer: 28,
  contentLeft: 46,
  contentRight: 547,
} as const;

export const PDF_HEADER = {
  logo: { x: 28, y: 32, height: 46 },
  /* The QR block is right-aligned: the code sits above its caption, both ending
   * on the same edge. `right` is where the symbol's own modules end — the
   * design places a screenshot that carries a quiet zone inside a wider box, so
   * that box's edge (567) would push a generated, quiet-zone-free symbol 5pt
   * past the one in the mockup. */
  qr: { right: 562, y: 32, size: 46 },
  qrCaptionBaseline: 90,
} as const;

/* Box the 3D screenshot is fitted into, expressed as a centre plus a maximum
 * extent. The design's product render is not centred on the content column — it
 * sits well right of it — and its own aspect ratio is whatever the viewer was
 * showing, so the design's fixed rectangle is treated as a bound to fit inside
 * rather than a frame to fill: cropping the product would be worse than
 * leaving air on one axis. */
export const PDF_IMAGE_BOX = {
  centerX: 356.5,
  centerY: 226,
  maxWidth: 265,
  maxHeight: 290,
} as const;

export const PDF_TITLE = {
  /* Baseline of the first line. The design sets the title beside the product
   * render rather than above it, optically centred on the image band. */
  baseline: 230,
  lineHeight: 32,
  /* Wrapped at the render's left edge rather than at the design's own 183pt box.
   * The box overlaps the render by 5pt, which the mockup gets away with because
   * its render is a photo with white air around the product — a config with an
   * opaque `scene.backgroundColor` paints that box edge to edge instead, and a
   * long product name would print on top of it. At the clearance the worst case
   * is a name that touches the render, never one that runs under it. None of the
   * shipped configs wraps differently for it. */
  maxWidth:
    PDF_IMAGE_BOX.centerX - PDF_IMAGE_BOX.maxWidth / 2 - PDF_MARGIN.contentLeft,
} as const;

export const PDF_SPEC = {
  /** Baseline of the "SPECIFIKACE" section heading. */
  headingBaseline: 366,
  /** The heavy rule that opens the specification block. */
  headingRuleY: 373,
} as const;

/* Gaps measured off the design, applied top-down while rows are emitted. */
export const SPEC_RHYTHM = {
  /** Rule -> baseline of the group label below it. */
  ruleToLabelBaseline: 16,
  /** Extra leading under the heavy opening rule, which breathes more. */
  firstGroupExtraLeading: 4,
  /** Group label baseline -> top of its first value row. */
  labelBaselineToRowTop: 11,
  /** Gap between two value rows of the same group. */
  rowGap: 8,
  /** Bottom of a group's last row -> the light rule that closes it. */
  rowBottomToGroupRule: 11,
  /** Bottom of the last row -> the heavy rule that closes the whole block. */
  rowBottomToBlockRule: 7,
  /** Heavy rule -> baseline of the quantity row. */
  blockRuleToQuantityBaseline: 20.5,
  /** Quantity baseline -> the rule under it. */
  quantityBaselineToRule: 8,
  /** Rule -> baseline of the total row, which the design gives a lot of air. */
  ruleToTotalBaseline: 38.5,
  /** Total baseline -> the rule that closes the document body. */
  totalBaselineToRule: 9.5,
} as const;

/** Row geometry per option-group kind, from the design's two row variants. */
export const SPEC_ROWS = {
  /* Colour and material rows: the option's artwork clipped into a circle, or a
   * flat fill of its colour where the config ships no artwork. The circle is
   * centred in a 45pt column rather than sitting on the content edge, which is
   * what lines its label up with the accessory rows' label. This variant has no
   * price slot at all — see `drawRowText`. */
  swatch: {
    height: 38,
    diameter: 38,
    columnWidth: 45,
    labelX: 107,
  },
  /* Accessory rows: a product thumbnail, its label and its surcharge. The
   * artwork fills the row and keeps the asset's aspect ratio — the design's box
   * *is* that ratio for the photos it was drawn against, so the `cover` fit
   * crops well under a percent instead of the third a square box cost. */
  thumbnail: {
    height: 42,
    labelX: 107,
    art: { width: 45, height: 42 },
  },
  /* Groups whose options carry neither artwork nor a colour — the shape most
   * configs have before their photos are produced. Indenting these to the
   * artwork column would leave a wide empty gutter down the page, so the label
   * starts on the content edge and the row only takes the height it needs. */
  text: {
    height: 24,
    labelX: 46,
  },
} as const;

/* A row's figure does not share the baseline of the label beside it, and the
 * offset is not derivable from the two type sizes: Figma centres each text
 * node's own line box in the row, the three rows give those boxes different
 * leading, and the three offsets come out different. So they are measured, like
 * everything else here — one computed rule got all three wrong.
 *
 * The reference render is 1 px per point, so anything between -0.5 and -0.75
 * reproduces the total's label identically; the rounder number is kept. */
export const FIGURE_OFFSET = {
  /** Accessory surcharge, below its label. */
  surcharge: 3.4,
  /** Quantity value, below its label. */
  quantity: 1,
  /** The total's label sits *above* its value, which is the anchored one. */
  totalLabel: -0.5,
} as const;

/* Type scale. Sizes were solved by matching rendered string widths against the
 * reference render: every string the design sets in Exo 2 lands within a point
 * of the width this font produces at these sizes. The design additionally sets
 * its body labels in Roboto, which the project does not ship and which
 * `docs/figma-parity.md` raises as an open question — they are set in Exo 2
 * here, 1-7pt wider than the mockup. */
export const PDF_TYPE = {
  title: { size: 24, weight: "bold" },
  sectionHeading: { size: 10, weight: "bold" },
  groupLabel: { size: 10, weight: "bold" },
  optionValue: { size: 10, weight: "normal" },
  optionPrice: { size: 14, weight: "bold" },
  quantityLabel: { size: 10, weight: "normal" },
  quantityValue: { size: 14, weight: "bold" },
  totalLabel: { size: 14, weight: "normal" },
  totalValue: { size: 20, weight: "bold" },
  qrCaption: { size: 8, weight: "normal" },
  footer: { size: 10, weight: "normal" },
  footerBrand: { size: 10, weight: "bold" },
} as const;

export const PDF_RULES = {
  /** Section boundaries: black, and visibly heavier than group rules. */
  heavy: { width: 1, color: PDF_COLORS.rule },
  /** Between option groups, and under the quantity row: `Grey 300`. */
  light: { width: 0.75, color: PDF_COLORS.grey300 },
} as const;

export const PDF_FOOTER_BASELINE = 826;

/* The 3D screenshot is embedded at 3x the printed size. It is by far the
 * largest payload in the file and an offer has to survive being emailed, so it
 * stays at 216 dpi rather than following the artwork below. */
export const PDF_IMAGE_SCALE = 3;

/* Row artwork and the brand logo carry the full print standard instead: they
 * occupy a few dozen points each, so 300 dpi costs tens of kilobytes, and at 3x
 * the swatch textures and accessory photos were the thing the design review
 * came back on. */
export const PDF_PRINT_SCALE = 300 / 72;
